import { ContractAPI } from '@contracts'
import type { MemberAttribute } from '@di-devs/member-feature-plugin/shared'
import type { Query, QueryKey } from '@tanstack/react-query'
import { initQueryClient } from '@ts-rest/react-query'
import { message } from 'antd'
import axios, { type AxiosRequestConfig } from 'axios'
import moment from 'moment'
import { COMMONS } from '.'
import * as CAMPAIGN_UTILS from './campaignUtils'

const viteAppApiUrl: string = import.meta.env.VITE_APP_API_URL // This should be set to the value of SITE_API_URI from the backend

export const SITE_URL = viteAppApiUrl

export const BASE_URL = SITE_URL + '/api'
export const SSE_URL = BASE_URL + '/sse'
export const ASSETS_PUBLIC_URL = SITE_URL + '/assets/public'
export const ASSETS_PRIVATE_URL = SITE_URL + '/assets/private'

export const MEMBERS_UPLOADS_URL = ASSETS_PUBLIC_URL + '/members/'
export const MEMBERS_CUSTOM_REGISTRATIONS_UPLOADS_URL = MEMBERS_UPLOADS_URL + 'memberAttribute'
export const SETTINGS_UPLOADS_URL = ASSETS_PUBLIC_URL + '/settings/'
export const MESSAGE_STORAGE_URL = ASSETS_PRIVATE_URL + '/line'
export const POSTAL_CODE_URL = 'https://yubinbango.github.io/yubinbango-data/data'
export const PUBLIC_HOLIDAY_URL = 'https://holidays-jp.github.io/api/v1/date.json'

/* BACKEND API URLS */
export const API_ADMIN_LOGIN = BASE_URL + '/login'
export const API_ADMIN_LOGOUT = BASE_URL + '/logout'
export const API_ADMIN_DAILY_STATS = BASE_URL + '/m/dailyStats'
export const API_ADMIN_GIFTS = BASE_URL + '/m/gifts'
export const API_ADMIN_CAMPAIGN_GIFTS = BASE_URL + '/m/campaign-gifts'
export const API_ADMIN_OCCURRENCES = BASE_URL + '/m/occurrences'
export const API_ADMIN_REGISTRATIONS = BASE_URL + '/m/registrations'
export const API_ADMIN_CAMPAIGN_REGISTRATIONS = BASE_URL + '/m/campaign-registrations'
export const API_ADMIN_HISTORY_REGISTRATIONS = BASE_URL + '/m/attended'
export const API_ADMIN_MEMBERS = BASE_URL + '/m/members'
export const API_ADMIN_CHATS = BASE_URL + '/m/members/chats'
export const API_ADMIN_SETTINGS = BASE_URL + '/settings'
export const API_ADMIN_RICHMENUS = BASE_URL + '/m/richmenus'
export const API_ADMIN_CAMPAIGNS = BASE_URL + '/campaigns'
export const API_ADMIN_SPECTATORS = BASE_URL + '/m/spectators'
export const API_ADMIN_MEMBER_ATTRIBUTES = BASE_URL + '/m/member-attributes'
export const API_CLIENT = BASE_URL + '/liff'

export const API_ADMIN_RICHMENU_TABGROUPS = BASE_URL + '/richmenutabgroups'

/* SOCKET PATH */
export const SOCKET_PATH = '/socket.io'

/* QUERY KEY */
export const QUERY_KEY_ADMIN_PUBLIC_SETTINGS = 'ADMIN_PUBLIC_SETTINGS'
export const QUERY_KEY_ADMIN_AUTH = 'ADMIN_AUTH'
export const QUERY_KEY_ADMIN_DAILY_STATS = 'ADMIN_DAILY_STATS'
export const QUERY_KEY_ADMIN_CAMPAIGN_TEMPLATES = 'ADMIN_CAMPAIGN_TEMPLATES'
export const QUERY_KEY_ADMIN_CAMPAIGN_TEMPLATE_DETAIL = 'ADMIN_CAMPAIGN_TEMPLATE_DETAIL'
export const QUERY_KEY_ADMIN_CAMPAIGNS = 'ADMIN_CAMPAIGNS'
export const QUERY_KEY_ADMIN_CAMPAIGN_DETAIL = 'ADMIN_CAMPAIGN_DETAIL'
export const QUERY_KEY_ADMIN_GIFT_TEMPLATE_DETAIL = 'GIFT_TEMPLATE_DETAIL'
export const QUERY_KEY_ADMIN_OCCURRENCE_DETAIL = 'ADMIN_OCCURRENCE_DETAIL'
export const QUERY_KEY_ADMIN_CAMPAIGN_REGISTRATIONS = 'ADMIN_CAMPAIGN_REGISTRATIONS'
export const QUERY_KEY_ADMIN_HISTORY_REGISTRATIONS = 'ADMIN_GET_HISTORY_ATTENDED'
export const QUERY_KEY_ADMIN_REGISTRATION_DETAIL = 'ADMIN_REGISTRATION_DETAIL'
export const QUERY_KEY_ADMIN_MEMBERS = 'ADMIN_MEMBERS'
export const QUERY_KEY_ADMIN_MEMBERS_LIST = 'ADMIN_MEMBERS_LIST'
export const QUERY_KEY_ADMIN_MEMBER_DETAIL = 'ADMIN_MEMBER_DETAIL'
export const QUERY_KEY_ADMIN_MEMBER_EVENT_HISTORY = 'ADMIN_MEMBER_EVENT_HISTORY'
export const QUERY_KEY_ADMIN_CHATS = 'ADMIN_CHATS'
export const QUERY_KEY_ADMIN_RICHMENUS = 'ADMIN_RICHMENUS'
export const QUERY_KEY_ADMIN_RICHMENU_TEMPLATES = 'ADMIN_RICHMENU_TEMPLATES'
export const QUERY_KEY_ADMIN_RICHMENU_TABGROUPS = 'ADMIN_RICHMENU_TABGROUPS'
export const QUERY_KEY_ADMIN_CAMPAIGN_QUESTIONS = 'ADMIN_CAMPAIGN_QUESTIONS'
export const QUERY_KEY_ADMIN_MEMBER_ATTRIBUTES = 'ADMIN_MEMBER_ATTRIBUTES'
export const QUERY_KEY_ADMIN_SPECTATORS = 'ADMIN_SPECTATORS'
export const QUERY_KEY_ADMIN_SPECTATOR_CANDIDATES = 'ADMIN_SPECTATOR_CANDIDATES'
export const QUERY_KEY_CLIENT_PERSONAL_INFO = 'CLIENT_PERSONAL_INFO'
export const QUERY_KEY_CLIENT_CAMPAIGN_QUESTIONS = 'CLIENT_CAMPAIGN_QUESTIONS'
export const QUERY_KEY_CLIENT_CAMPAIGNS = 'CLIENT_CAMPAIGNS'
export const QUERY_KEY_CLIENT_CAMPAIGN_DETAIL = 'CLIENT_CAMPAIGN_DETAIL'
export const QUERY_KEY_CLIENT_GIFTS = 'CLIENT_GIFTS'
export const QUERY_KEY_CLIENT_MY_REGISTRATIONS = 'CLIENT_MY_REGISTRATIONS'
export const QUERY_KEY_CLIENT_MY_REGISTRATION_DETAIL = 'CLIENT_MY_REGISTRATION_DETAIL'
export const QUERY_KEY_CLIENT_MY_CAMPAIGN_REGISTRATION_DETAIL =
	'CLIENT_MY_CAMPAIGN_REGISTRATION_DETAIL'
export const QUERY_KEY_CLIENT_MEMBER_ATTRIBUTES = 'CLIENT_MEMBER_ATTRIBUTES'
export const QUERY_KEY_CLIENT_COUPONS = 'CLIENT_COUPONS'
export const QUERY_KEY_CLIENT_EVENTS = 'CLIENT_EVENTS'
export const QUERY_KEY_CLIENT_EVENT_DETAIL = 'CLIENT_EVENT_DETAIL'
export const QUERY_KEY_CLIENT_MY_EVENT_REGISTRATIONS = 'CLIENT_MY_EVENT_REGISTRATIONS'
export const QUERY_KEY_LOGO = 'LOGO'
export const QUERY_KEY_FAVICON = 'FAVICON'
export const QUERY_KEY_STORE_PIC = 'STORE_PIC'
export const QUERY_KEY_PUBLIC_SETTINGS = 'PUBLIC_SETTINGS'
export const QUERY_KEY_PUBLIC_HOLIDAYS = 'PUBLIC_HOLIDAYS'

export * from './constants'

export const tsRestClient = initQueryClient(ContractAPI, {
	baseUrl: SITE_URL,
	baseHeaders: {},
	credentials: 'include',
})

// TODO: Standardize server responses to correlate with frontend responses
// e.g. richmenu image upload to already published richmenu tab group should return something more descriptive than 500 status
// Server: RICHMENU_ALREADY_PUBLISHED_IMAGE_UPLOAD_ERROR_MSG => Frontend: Japanese error message
export const clientOnError = (
	error: unknown,
	query: Query<unknown, unknown, unknown, QueryKey>,
) => {
	if (!query.meta?.shouldBeHandledByGlobalErrorHandler) {
		return
	}
	const status = axios.isAxiosError(error) ? error.response?.status : undefined
	if (status === COMMONS.RESPONSE_PERMISSION_ERROR) {
		window.location.href = COMMONS.PERMISSION_ERROR_ROUTE
	} else if (status === COMMONS.RESPONSE_SESSION_ERROR) {
		message.warning({
			content: COMMONS.ERROR_SESSION_MSG,
			key: COMMONS.MESSAGE_SESSION_ERROR_KEY,
		})
		window.location.href = COMMONS.ADMIN_LOGIN_ROUTE
	} else {
		message.error({
			content: COMMONS.ERROR_SYSTEM_MSG,
			key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
		})
	}
}

/* AXIOS CONFIG */
const axiosInstance = axios.create({
	withCredentials: true,
	baseURL: SITE_URL,
	timeout: 10000,
})

axiosInstance.interceptors.response.use(
	(response) => {
		if (
			response.data &&
			response.data.success === false &&
			response.data.message === 'session does not exist'
		) {
			window.location.href = COMMONS.ADMIN_LOGIN_ROUTE
			// Return a rejected promise to stop further processing in the caller
			return Promise.reject(new Error('Session expired'))
		}
		return response
	},
	(error) => {
		if (
			error.response &&
			error.response.data &&
			error.response.data.success === false &&
			error.response.data.message === 'session does not exist'
		) {
			window.location.href = COMMONS.ADMIN_LOGIN_ROUTE
		}
		return Promise.reject(error)
	},
)

/* BACKEND API REQUESTS */
export async function ADMIN_LOGIN(data: any) {
	return await axiosInstance.post(API_ADMIN_LOGIN, data)
}

export async function ADMIN_LOGOUT() {
	return await axiosInstance.get(API_ADMIN_LOGOUT)
}

export async function ADMIN_GET_DAILY_STATS(
	filterStatsFrom: string,
	filterStatsTo: string | undefined,
) {
	const paramData = {
		params: {
			from: filterStatsFrom,
			to: filterStatsTo,
		},
	}

	return await axiosInstance.get(API_ADMIN_DAILY_STATS, paramData)
}

export async function ADMIN_GET_CAMPAIGN_DETAIL(
	campaignId: string,
	filterOccurrencesFrom: any,
	filterOccurrencesTo: any,
	search: any[],
) {
	const dataSearch = search?.map((item: { toISOString: () => any }) => item?.toISOString())
	const paramData = {
		params: {
			from: filterOccurrencesFrom,
			to: filterOccurrencesTo,
			search: dataSearch,
		},
	}

	return await axiosInstance.get(API_ADMIN_CAMPAIGNS + '/' + campaignId, paramData)
}

export async function ADMIN_GET_CAMPAIGN_TEMPLATES() {
	const paramData = {
		params: {
			includeDestroyed: false,
		},
	}

	return await axiosInstance.get(API_ADMIN_CAMPAIGNS + '/basic', paramData)
}

export async function ADMIN_GET_CAMPAIGN_TEMPLATE_DETAIL(campaignId: string | undefined) {
	const paramData = {
		params: {
			includeDestroyed: false,
		},
	}

	return await axiosInstance.get(API_ADMIN_CAMPAIGNS + '/' + campaignId, paramData)
}

export async function ADMIN_GET_CAMPAIGNS({
	pageParam = 1,
	pp = 20,
	sortBy = 'campaignId',
	sortOrder = 'DESC',
}: {
	pageParam?: number
	pp?: number
	sortBy?: string
	sortOrder?: string
} = {}) {
	const paramData = {
		params: {
			limit: pp,
			page: pageParam,
			sortBy,
			sortOrder,
		},
	}

	return await axiosInstance.get(API_ADMIN_CAMPAIGNS, paramData)
}
export async function ADMIN_CREATE_CAMPAIGN(data: { formData: any }) {
	const headerData = {
		headers: {
			'content-type': 'multipart/form-data',
		},
	}

	return await axiosInstance.post(API_ADMIN_CAMPAIGNS, data?.formData, headerData)
}
export async function ADMIN_UPDATE_CAMPAIGN(data: { campaignId: string; formData: any }) {
	const headerData = {
		headers: {
			'content-type': 'multipart/form-data',
		},
	}

	return await axiosInstance.put(
		API_ADMIN_CAMPAIGNS + '/' + data?.campaignId,
		data?.formData,
		headerData,
	)
}

export async function ADMIN_DELETE_CAMPAIGN(data: { campaignId: string }) {
	return await axiosInstance.delete(API_ADMIN_CAMPAIGNS + '/' + data?.campaignId)
}

export async function ADMIN_GET_Gift_TEMPLATES() {
	const paramData = {
		params: {
			includeDestroyed: false,
		},
	}

	return await axiosInstance.get(API_ADMIN_GIFTS + '/basic', paramData)
}
export async function ADMIN_GET_GIFT_TEMPLATE_DETAIL(giftId: string) {
	const paramData = {
		params: {
			includeDestroyed: false,
		},
	}

	return await axiosInstance.get(API_ADMIN_GIFTS + '/' + giftId, paramData)
}

export async function ADMIN_GET_CAMPAIGN_GIFTS({
	pageParam = 1,
	queryKey,
}: {
	pageParam?: number
	queryKey: QueryKey
}) {
	const paramData = {
		params: {
			campaignId: queryKey[1] as string,
			pp: 20,
			p: pageParam,
		},
	}

	return await axiosInstance.get(API_ADMIN_CAMPAIGN_GIFTS, paramData)
}

export async function ADMIN_CREATE_CAMPAIGN_GIFTS(data: { formData: any }) {
	const headerData = {
		headers: {
			'content-type': 'multipart/form-data',
		},
	}

	return await axiosInstance.post(API_ADMIN_CAMPAIGN_GIFTS, data?.formData, headerData)
}

export async function ADMIN_UPDATE_CAMPAIGN_GIFTS(data: { giftId: string; formData: any }) {
	const headerData = {
		headers: {
			'content-type': 'multipart/form-data',
		},
	}

	return await axiosInstance.put(
		API_ADMIN_CAMPAIGN_GIFTS + '/' + data?.giftId,
		data?.formData,
		headerData,
	)
}

export async function ADMIN_UPDATE_GIFT_ORDER(data: any) {
	return await axiosInstance.put(API_ADMIN_GIFTS, data)
}
export async function ADMIN_UPDATE_GIFT(data: any) {
	return await axiosInstance.put(API_ADMIN_GIFTS + '/update', { gifts: data })
}
export async function ADMIN_POST_REGISTER_WINNERS(data: any) {
	return await axiosInstance.post(API_ADMIN_CAMPAIGNS + '/register-winners', data)
}
export async function ADMIN_POST_AUTOMATIC_REGISTER_WINNERS(data: any) {
	return await axiosInstance.post(API_ADMIN_CAMPAIGNS + '/automatic-register-winners', data)
}
export async function ADMIN_DELETE_GIFT(data: { giftId: string }) {
	return await axiosInstance.delete(API_ADMIN_GIFTS + '/' + data?.giftId)
}

export async function ADMIN_CREATE_OCCURRENCE(data: any) {
	return await axiosInstance.post(API_ADMIN_OCCURRENCES, data)
}

export async function ADMIN_GET_OCCURRENCE_DETAIL(occurrenceId: string) {
	return await axiosInstance.get(API_ADMIN_OCCURRENCES + '/' + occurrenceId)
}

export async function ADMIN_GET_CAMPAIGN_REGISTRATIONS(campaignId: string) {
	return await axiosInstance.get(API_ADMIN_CAMPAIGN_REGISTRATIONS + '/' + campaignId)
}

export async function ADMIN_UPDATE_OCCURRENCE(data: { occurrenceId: string }) {
	return await axiosInstance.put(API_ADMIN_OCCURRENCES + '/' + data?.occurrenceId, data)
}

export async function ADMIN_DELETE_OCCURRENCE(data: { occurrenceId: string }) {
	return await axiosInstance.delete(API_ADMIN_OCCURRENCES + '/' + data?.occurrenceId)
}

export async function ADMIN_DELETE_BATCH_OCCURRENCES(data: any) {
	return await axiosInstance.delete(API_ADMIN_OCCURRENCES, { data: data })
}

export async function ADMIN_CREATE_REGISTRATION_MANUAL(data: any) {
	return await axiosInstance.post(API_ADMIN_REGISTRATIONS, data)
}
export async function ADMIN_CREATE_REGISTRATION_CAMPAIGN_MANUAL(data: any) {
	return await axiosInstance.post(API_ADMIN_CAMPAIGN_REGISTRATIONS, data)
}

export async function ADMIN_UPDATE_REGISTRATION(data: any) {
	return await axiosInstance.put(API_ADMIN_REGISTRATIONS + '/edit', data)
}
export async function ADMIN_UPDATE_ATTENDED_REGISTRATION(data: any) {
	return await axiosInstance.put(API_ADMIN_REGISTRATIONS + '/update-attended', data)
}

export async function ADMIN_GET_HISTORY_ATTENDED(numberId: string | number | undefined) {
	return await axiosInstance.get(API_ADMIN_HISTORY_REGISTRATIONS + '/' + numberId)
}

export async function ADMIN_CANCEL_REGISTRATION(data: { registrationId: string }) {
	const param = {
		data: {},
	}

	return await axiosInstance.delete(
		API_ADMIN_REGISTRATIONS + '/' + data.registrationId + '/cancel',
		param,
	)
}

export async function ADMIN_CONFIRM_REGISTRATION(data: any) {
	return await axiosInstance.post(API_ADMIN_REGISTRATIONS + '/qr', data)
}

export async function ADMIN_RECONFIRM_REGISTRATION(data: any) {
	return await axiosInstance.put(API_ADMIN_REGISTRATIONS + '/confirm', data)
}

export async function ADMIN_EXPORT_REGISTRATIONS(data: any) {
	return await axiosInstance.post(API_ADMIN_REGISTRATIONS + '/csv', data)
}

export async function ADMIN_GET_MEMBERS(options: {
	paginationPerPage?: any
	paginationPage?: any
	paginationSort?: any
	paginationSortKey?: any
	filterIsCampaignPage?: any
	filters?: Record<string, unknown>
	[key: string]: any
}) {
	const {
		paginationPerPage,
		paginationPage,
		paginationSort,
		paginationSortKey,
		filterIsCampaignPage,
		filters,
		...restParams
	} = options

	// Clean up keys with 'Filter' in the name
	const cleanFilterParams: Record<string, any> = {}
	for (const [key, value] of Object.entries(filters ?? restParams)) {
		const cleanKeyName = key.replace('Filter', '')
		cleanFilterParams[cleanKeyName as string] = value
	}
	const paramData = {
		params: {
			pp: paginationPerPage,
			p: paginationPage,
			sort: paginationSort,
			sortKey: paginationSortKey,
			...(filterIsCampaignPage
				? {
						isCampaign: true,
					}
				: {}),
			...cleanFilterParams,
		},
	}

	return await axiosInstance.get(API_ADMIN_MEMBERS, paramData)
}

export async function ADMIN_GET_MEMBER(data: { memberId: any }) {
	return await axiosInstance.get(API_ADMIN_MEMBERS + '/' + data.memberId)
}

export async function ADMIN_GET_MEMBER_DETAIL(data: { memberId: any }) {
	return await axiosInstance.get(API_ADMIN_MEMBERS + '/' + data.memberId + '/detail')
}

export async function ADMIN_GET_MEMBER_EVENT_DETAIL(data: { memberId: any }) {
	return await axiosInstance.get(BASE_URL + '/m/event/member/' + data.memberId + '/detail')
}

export async function ADMIN_GET_MEMBER_BY_CODE(data: { memberCode: any }) {
	return await axiosInstance.get(API_ADMIN_MEMBERS + '/code/' + data.memberCode)
}

export async function ADMIN_DELETE_MEMBER(data: { memberId: string }) {
	return await axiosInstance.delete(API_ADMIN_MEMBERS + '/' + data.memberId)
}

export async function ADMIN_RENEW_MEMBER(data: FormData | undefined) {
	if (data instanceof FormData) {
		return await axiosInstance.put(API_ADMIN_MEMBERS + '/' + data.get('memberId'), data, {
			headers: {
				'content-type': 'multipart/form-data',
			},
		})
	}
	if (!data) {
		throw new Error('data is required for ADMIN_RENEW_MEMBER')
	}
	return await axiosInstance.put(
		API_ADMIN_MEMBERS + '/' + (data as Record<string, unknown>).memberId,
		data,
	)
}

export async function ADMIN_UPDATE_MEMBERSHIP_MANUAL(data: {
	memberId: string | number
	paymentAction: string
	activeUntil: string | Date | null
}) {
	return await axiosInstance.put(BASE_URL + '/members/' + data.memberId + '/membership', data)
}

export async function GET_MEMBER_PAYMENTS(memberId: string | number) {
	return await axiosInstance.get(API_ADMIN_MEMBERS + '/' + memberId + '/payments')
}

export async function ADMIN_CHECK_MEMBER(data: any) {
	return await axiosInstance.post(API_ADMIN_MEMBERS + '/barcode', data)
}

export async function ADMIN_EXPORT_MEMBERS(data: any) {
	const clientTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
	return await axiosInstance.post(API_ADMIN_MEMBERS + '/csv', {
		...data,
		timeZone: clientTimezone,
	})
}

export async function generateMemberCSV(data: any) {
	const clientTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
	return await axiosInstance.post(API_ADMIN_MEMBERS + '/csv', {
		...data,
		timeZone: clientTimezone,
	})
}

export async function generateMemberVisitCSV(data: { from: string; to: string; password: string }) {
	const clientTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
	return await axiosInstance.post(API_ADMIN_MEMBERS + '/visits/csv', {
		...data,
		timeZone: clientTimezone,
	})
}

export async function ADMIN_APPROVE_MEMBERSHIP(data: { memberId: string }) {
	return await axiosInstance.post(API_ADMIN_MEMBERS + '/' + data.memberId + '/approve')
}

export async function ADMIN_GET_CHATS(member: { memberId: string }) {
	return await axiosInstance.get(API_ADMIN_CHATS + '/' + member?.memberId)
}

export async function ADMIN_SEND_CHAT(data: { memberId: string }) {
	return await axiosInstance.post(API_ADMIN_CHATS + '/' + data.memberId, data)
}

export async function ADMIN_GET_CAMPAIGN_QUESTIONS() {
	const paramData = {
		params: {},
	}

	return await axiosInstance.get(API_ADMIN_CAMPAIGNS + '/questions', paramData)
}

export async function ADMIN_GET_CAMPAIGN_QUESTIONS_NEW({ campaignId }: { campaignId: string }) {
	const paramData = {
		params: {},
	}

	return await axiosInstance.get(API_ADMIN_CAMPAIGNS + `/list-questions/${campaignId}`, paramData)
}

export async function ADMIN_CREATE_CAMPAIGN_QUESTION(data: any) {
	return await axiosInstance.post(API_ADMIN_CAMPAIGNS + '/questions', data)
}

export async function ADMIN_CREATE_CAMPAIGN_QUESTION_NEW(data: any) {
	return await axiosInstance.post(API_ADMIN_CAMPAIGNS + '/create-questions', data)
}

export async function ADMIN_UPDATE_CAMPAIGN_QUESTION(data: { questionId: string }) {
	return await axiosInstance.put(API_ADMIN_CAMPAIGNS + '/questions/' + data?.questionId, data)
}

export async function ADMIN_UPDATE_CAMPAIGN_QUESTION_ORDER(data: any) {
	return await axiosInstance.put(API_ADMIN_CAMPAIGNS + '/questions', data)
}

export async function ADMIN_DELETE_CAMPAIGN_QUESTION(data: { questionId: string }) {
	return await axiosInstance.delete(API_ADMIN_CAMPAIGNS + '/questions/' + data?.questionId)
}

export async function ADMIN_GET_CAMPAIGN_CANDIDATES(data: AxiosRequestConfig<any> | undefined) {
	return await axiosInstance.get(API_ADMIN_CAMPAIGNS + '/candidates', data)
}

export async function ADMIN_UPDATE_CAMPAIGN_CANDIDATE(data: any) {
	return await axiosInstance.post(API_ADMIN_CAMPAIGNS + '/winners', data)
}

export async function ADMIN_EXPORT_CAMPAIGN_WINNERS(data: any) {
	return await axiosInstance.post(API_ADMIN_CAMPAIGNS + '/winners/csv', data)
}

export async function ADMIN_RESET_CAMPAIGN_WINNERS(data: any) {
	return await axiosInstance.post(API_ADMIN_CAMPAIGNS + '/winners/reset', data)
}

export async function ADMIN_GET_SPECTATORS() {
	const paramData = {
		params: {},
	}

	return await axiosInstance.get(API_ADMIN_SPECTATORS, paramData)
}

export async function ADMIN_GET_SPECTATOR_CANDIDATES() {
	const paramData = {
		params: {},
	}

	return await axiosInstance.get(API_ADMIN_SPECTATORS + '/candidates', paramData)
}

export async function ADMIN_REMOVE_SPECTATOR(data: { spectatorId: string }) {
	return await axiosInstance.delete(API_ADMIN_SPECTATORS + '/' + data.spectatorId)
}

export async function ADMIN_ADD_SPECTATOR(data: any) {
	return await axiosInstance.post(API_ADMIN_SPECTATORS, data)
}

export async function GET_LOGO() {
	return await axiosInstance.get(BASE_URL + '/logo')
}

export async function ADMIN_UPLOAD_LOGO(logo: string | Blob) {
	const headerData = {
		headers: {
			'content-type': 'multipart/form-data',
		},
	}

	const formData = new FormData()
	formData.append('picUrl', logo)

	return await axiosInstance.put(BASE_URL + '/logo', formData, headerData)
}

export async function GET_FAVICON() {
	return await axiosInstance.get(BASE_URL + '/favicon')
}

export async function ADMIN_UPLOAD_FAVICON(favicon: string | Blob) {
	const headerData = {
		headers: {
			'content-type': 'multipart/form-data',
		},
	}

	const formData = new FormData()
	formData.append('picUrl', favicon)

	return await axiosInstance.put(BASE_URL + '/favicon', formData, headerData)
}

export async function GET_STORE_PIC() {
	return await axiosInstance.get(BASE_URL + '/store/pic')
}

export async function ADMIN_UPLOAD_STORE_PIC(logo: string | Blob) {
	const headerData = {
		headers: {
			'content-type': 'multipart/form-data',
		},
	}

	const formData = new FormData()
	formData.append('picUrl', logo)

	return await axiosInstance.put(BASE_URL + '/store/pic', formData, headerData)
}

export async function GET_ADMIN_PUBLIC_SETTINGS() {
	return await axiosInstance.get(BASE_URL + '/publicSettings')
}

export async function GET_PUBLIC_SETTINGS() {
	return await axiosInstance.get(BASE_URL + '/settings')
}

export async function ADMIN_UPDATE_SETTINGS_BATCH(data: any) {
	return await axiosInstance.put(API_ADMIN_SETTINGS, data)
}

export async function ADMIN_UPDATE_PUBLIC_SETTINGS(data: { key: string }) {
	return await axiosInstance.put(API_ADMIN_SETTINGS + '/' + data.key, data)
}

export async function ADMIN_GET_RICHMENUS() {
	return await axiosInstance.get(API_ADMIN_RICHMENUS)
}

export async function ADMIN_UPDATE_RICHMENU(data: { [x: string]: string | Blob }) {
	const headerData = {
		headers: {
			'content-type': 'multipart/form-data',
		},
	}

	const formData = new FormData()
	Object.keys(data).forEach((key) => {
		if (typeof data[key] === 'object') {
			// If the value is an object, convert it to a JSON string.
			formData.append(key, JSON.stringify(data[key]))
		} else {
			// For non-object types, append directly.
			formData.append(key, data[key])
		}
	})
	return await axiosInstance.put(API_ADMIN_RICHMENUS, formData, headerData)
}

export async function ADMIN_UPDATE_RICHMENU_VISIBILITY(data: any) {
	return await axiosInstance.put(API_ADMIN_RICHMENUS + '/props', data)
}

export async function ADMIN_DELETE_RICHMENU(data: { type: any }) {
	const param = {
		data: {
			type: data.type,
		},
	}

	return await axiosInstance.delete(API_ADMIN_RICHMENUS, param)
}

export async function CLIENT_GET_PERSONAL_INFO(accessToken: string | undefined) {
	const headerData = { headers: { 'access-token': accessToken } }

	return await axiosInstance.get(API_CLIENT + '/personal', headerData)
}

export async function CLIENT_GET_MEMBER_ATTRIBUTES(accessToken: string | undefined) {
	const headerData = { headers: { 'access-token': accessToken } }

	return await axiosInstance.get(API_CLIENT + '/member-attributes', headerData)
}

export async function CLIENT_REGISTER_PROFILE(data: { accessToken: any }) {
	const headerData = {
		headers: { 'access-token': data.accessToken },
	}

	return await axiosInstance.post(API_CLIENT + '/personal', data, headerData)
}

export async function CLIENT_REGISTER_PROFILE_NEW(data: { accessToken: any; formData: any }) {
	const headerData = {
		headers: {
			'access-token': data?.accessToken,
			'content-type': 'multipart/form-data',
		},
	}

	return await axiosInstance.post(API_CLIENT + '/personal', data?.formData, headerData)
}

export async function CLIENT_UPDATE_PROFILE(data: { accessToken: any }) {
	const headerData = {
		headers: {
			'access-token': data.accessToken,
		},
	}

	return await axiosInstance.post(API_CLIENT + '/personal', data, headerData)
}

export async function CLIENT_GET_CAMPAIGN_QUESTIONS(accessToken: any) {
	const headerData = {
		headers: { 'access-token': accessToken },
	}

	return await axiosInstance.get(API_CLIENT + '/campaigns/questions', headerData)
}

export async function CLIENT_LINK_PAPER_MEMBER(data: { accessToken: any; formData: FormData }) {
	return await axiosInstance.post(API_CLIENT + '/link-paper-member', data.formData, {
		headers: { 'access-token': data.accessToken },
	})
}

export async function CLIENT_GET_CAMPAIGN_QUESTIONS_NEW({
	accessToken,
	campaignId,
}: {
	accessToken: string
	campaignId: string
}) {
	const headerData = {
		headers: { 'access-token': accessToken },
	}

	return await axiosInstance.get(API_CLIENT + `/campaigns/questions/${campaignId}`, headerData)
}

export async function CLIENT_APPLY_CAMPAIGN(data: { accessToken: any }) {
	const headerData = {
		headers: {
			'access-token': data.accessToken,
		},
	}

	return await axiosInstance.post(API_CLIENT + '/campaigns/answers', data, headerData)
}

export async function CLIENT_GET_CAMPAIGNS({
	pageParam = 1,
	queryKey,
}: {
	pageParam?: number
	queryKey: QueryKey
}) {
	const headerData = {
		headers: { 'access-token': queryKey[1] as string },
		params: {
			pp: 20,
			p: pageParam,
		},
	}

	return await axiosInstance.get(API_CLIENT + '/campaigns', headerData)
}

export async function CLIENT_GET_CAMPAIGN_DETAIL(accessToken: any, campaignId: string) {
	const paramData = {
		headers: { 'access-token': accessToken },
		params: {
			from: moment().format('YYYY-MM-DD HH:mm'),
			to: moment()
				.add(1, 'month')
				.endOf('month')
				.hours(23)
				.minutes(59)
				.format('YYYY-MM-DD HH:mm'),
		},
	}

	return await axiosInstance.get(API_CLIENT + '/campaigns/' + campaignId, paramData)
}

export async function CLIENT_GET_GIFTS({
	pageParam = 1,
	queryKey,
}: {
	pageParam?: number
	queryKey: QueryKey
}) {
	const headerData = {
		headers: { 'access-token': queryKey[1] as string },
		params: {
			pp: 20,
			p: pageParam,
			campaignId: queryKey[2] as string,
		},
	}

	return await axiosInstance.get(API_CLIENT + '/campaigns-gifts', headerData)
}

export async function CLIENT_RESERVE_CAMPAIGN_OCCASION(data: { accessToken: any; formData: any }) {
	const headerData = {
		headers: {
			'access-token': data?.accessToken,
			'content-type': 'multipart/form-data',
		},
	}

	return await axiosInstance.post(
		API_CLIENT + '/campaign-registerForEvent',
		data?.formData,
		headerData,
	)
}

export async function CLIENT_GET_MY_REGISTRATIONS(accessToken: any) {
	const headerData = {
		headers: { 'access-token': accessToken },
	}

	return await axiosInstance.get(API_CLIENT + '/registrations', headerData)
}
export async function CLIENT_GET_MY_CAMPAIGN_REGISTRATIONS(accessToken: any) {
	const headerData = {
		headers: { 'access-token': accessToken },
	}

	return await axiosInstance.get(API_CLIENT + '/campaigns-registrations', headerData)
}

export async function CLIENT_GET_MY_REGISTRATION_DETAIL(
	accessToken: any,
	registrationId: string | undefined,
) {
	const headerData = {
		headers: { 'access-token': accessToken },
	}

	return await axiosInstance.get(API_CLIENT + '/registrations/' + registrationId, headerData)
}

export async function CLIENT_GET_MY_CAMPAIGN_REGISTRATION_DETAIL(
	accessToken: any,
	registrationId: string,
) {
	const headerData = {
		headers: { 'access-token': accessToken },
	}

	return await axiosInstance.get(
		API_CLIENT + '/campaign-registrations/' + registrationId,
		headerData,
	)
}

export async function CLIENT_CANCEL_REGISTRATION(data: {
	accessToken: any
	registrationId: string
}) {
	const clientTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone

	const param = {
		headers: { 'access-token': data.accessToken },
		data: { timeZone: clientTimezone },
	}

	return await axiosInstance.delete(API_CLIENT + '/registrations/' + data.registrationId, param)
}

export const GET_PUBLIC_HOLIDAYS = async () => {
	return await axios.get(PUBLIC_HOLIDAY_URL)
}

export const GET_ADDRESS_BY_POSTAL_CODE = (postalCode: string) => {
	const p3 = postalCode.substr(0, 3)

	return fetch(`${POSTAL_CODE_URL}/${p3}.js`)
		.then((response) => response.text())
		.then((text) => text)
		.catch(() => {})
}

export const GET_CUSTOM_REGISTRATION_UPLOAD_PATH = (
	memberAttributeId: MemberAttribute['memberAttributeId'],
) => {
	return MEMBERS_CUSTOM_REGISTRATIONS_UPLOADS_URL + memberAttributeId
}

export async function CLIENT_GET_COUPONS(
	accessToken: string,
	memberId: number,
	couponType?: string | string[],
) {
	const headerData = {
		headers: { 'access-token': accessToken },
	}

	let url = `${API_CLIENT}/members/${memberId}/coupons`
	if (couponType) {
		const typeParam = Array.isArray(couponType) ? couponType.join(',') : couponType
		url += `?type=${encodeURIComponent(typeParam)}`
	}

	return await axiosInstance.get(url, headerData)
}

export async function CLIENT_MARK_COUPON_USED(accessToken: string, couponId: string) {
	const headerData = {
		headers: { 'access-token': accessToken },
	}

	return await axiosInstance.patch(`${API_CLIENT}/coupons/${couponId}/use`, {}, headerData)
}

export async function CLIENT_GET_EVENTS(accessToken: string, limit = 20, offset = 0) {
	const headerData = {
		headers: { 'access-token': accessToken },
	}

	return await axiosInstance.get(
		`${API_CLIENT}/events?limit=${limit}&offset=${offset}`,
		headerData,
	)
}

export async function CLIENT_GET_EVENT_DETAIL(accessToken: string, eventId: number) {
	const headerData = {
		headers: { 'access-token': accessToken },
	}

	return await axiosInstance.get(`${API_CLIENT}/events/${eventId}`, headerData)
}

export async function CLIENT_REGISTER_EVENT(accessToken: string, data: any) {
	const headerData = {
		headers: { 'access-token': accessToken },
	}

	return await axiosInstance.post(`${API_CLIENT}/events/register`, data, headerData)
}

export async function CLIENT_PAY_MEMBERSHIP_FEE(accessToken: string, sourceId: string) {
	const headerData = {
		headers: { 'access-token': accessToken },
	}

	const data = {
		sourceId,
	}

	return await axiosInstance.post(`${API_CLIENT}/payments/membership-fee`, data, headerData)
}

export async function CLIENT_CANCEL_EVENT(accessToken: string, eventRegistrationId: number) {
	const headerData = {
		headers: { 'access-token': accessToken },
	}

	return await axiosInstance.post(
		`${API_CLIENT}/events/cancel`,
		{ eventRegistrationId },
		headerData,
	)
}

export async function CLIENT_GET_PUBLIC_PAYMENT_INFO(code: string) {
	return await axiosInstance.get(
		`${BASE_URL}/public/payment/info?code=${encodeURIComponent(code)}`,
	)
}

export async function CLIENT_PAY_PUBLIC_MEMBERSHIP_FEE_VIA_CODE(code: string, sourceId: string) {
	return await axiosInstance.post(`${BASE_URL}/public/payment/pay`, {
		code,
		sourceId,
	})
}

export async function CLIENT_GET_MY_EVENT_REGISTRATIONS(
	accessToken: string,
	limit = 20,
	offset = 0,
) {
	const headerData = {
		headers: { 'access-token': accessToken },
	}

	return await axiosInstance.get(
		`${API_CLIENT}/events/my-registrations?limit=${limit}&offset=${offset}`,
		headerData,
	)
}

// Dummy exports for unused pages
export const CATEGORIES_UPLOADS_URL = ASSETS_PUBLIC_URL + '/categories/'
export const OCCASIONS_UPLOADS_URL = ASSETS_PUBLIC_URL + '/occasions/'

export const QUERY_KEY_ADMIN_CATEGORY_TAGS = 'ADMIN_CATEGORY_TAGS'
export const QUERY_KEY_ADMIN_CATEGORY_AREAS = 'ADMIN_CATEGORY_AREAS'
export const QUERY_KEY_ADMIN_CATEGORIES = 'ADMIN_CATEGORIES'
export const QUERY_KEY_ADMIN_CATEGORY_DETAIL = 'ADMIN_CATEGORY_DETAIL'
export const QUERY_KEY_ADMIN_CATEGORY_LIST = 'ADMIN_CATEGORY_LIST'
export const QUERY_KEY_ADMIN_OCCASION_LIST = 'ADMIN_OCCASION_LIST'
export const QUERY_KEY_ADMIN_OCCASION_DETAIL = 'ADMIN_OCCASION_DETAIL'

export const IS_CATEGORY_VERSION = false

export async function ADMIN_GET_CATEGORY_TAGS() {
	return Promise.resolve([])
}
export async function ADMIN_GET_CATEGORY_AREAS() {
	return Promise.resolve([])
}
export async function ADMIN_GET_CATEGORY_LIST() {
	return Promise.resolve([])
}
export async function ADMIN_GET_OCCASION_LIST() {
	return Promise.resolve([])
}
export async function CLIENT_RESERVE_OCCASION() {
	return Promise.resolve([])
}

// ===== CAMPAIGN ADMIN APIs =====

export const QUERY_KEY_ADMIN_CAMP_ADD = 'ADMIN_CAMP_ADD'
export const QUERY_KEY_ADMIN_CAMP_DETAILS = 'ADMIN_CAMP_DETAILS'
export const QUERY_KEY_ADMIN_CAMP_EDIT = 'ADMIN_CAMP_DETAILS'

export async function ADMIN_GET_COUPON_ALL() {
	return await axiosInstance.get(BASE_URL + '/m/coupon-masters')
}

export async function ADMIN_ADD_CAMP(data: Record<string, unknown>) {
	return await axiosInstance.post(
		BASE_URL + '/campaigns',
		CAMPAIGN_UTILS.mapFormToPayload(data as Partial<CAMPAIGN_UTILS.CampaignFormValues>),
	)
}

export async function ADMIN_FILTER_CAMP(data: Record<string, unknown>) {
	return await axiosInstance.post(
		BASE_URL + '/campaigns/preview',
		CAMPAIGN_UTILS.mapFormToPayload(data as Partial<CAMPAIGN_UTILS.CampaignFormValues>)
	)
}

export async function ADMIN_EDIT_CAMP(data: Record<string, unknown>) {
	const campaignId = data.campaignId
	return await axiosInstance.put(
		BASE_URL + '/campaigns/' + campaignId,
		CAMPAIGN_UTILS.mapFormToPayload(data as Partial<CAMPAIGN_UTILS.CampaignFormValues>),
	)
}

export async function ADMIN_GET_CAMP_DETAILS(campaignId: number | string) {
	return await axiosInstance.get(BASE_URL + '/campaigns/' + campaignId)
}

export async function ADMIN_GET_CAMP_ALL(
	data: { paginationPage?: number; paginationPerPage?: number } = {},
) {
	return await axiosInstance.get(BASE_URL + '/campaigns', {
		params: {
			page: data.paginationPage || 1,
			limit: data.paginationPerPage || 20,
		},
	})
}

export async function ADMIN_DELETE_CAMP(data: { campaignId: number | string }) {
	return await axiosInstance.delete(BASE_URL + '/campaigns/' + data.campaignId)
}

// ===== COUPON ADMIN APIs =====
export const QUERY_KEY_ADMIN_COUPON = 'ADMIN_COUPON'

export async function ADMIN_GET_COUPON(params: { couponId: string | number }) {
	return await axiosInstance.get(BASE_URL + `/coupons/masters/${params.couponId}`)
}

export async function ADMIN_ADD_COUPON(data: any) {
	return await axiosInstance.post(BASE_URL + '/coupons/masters', data)
}

export async function ADMIN_UPDATE_COUPON(data: any) {
	return await axiosInstance.put(BASE_URL + `/coupons/masters/${data.couponId}`, data)
}
